import "./styles/ScriptInputField.scss";

import { autocompletion, type CompletionContext } from "@codemirror/autocomplete";
import { esLint, javascript } from "@codemirror/lang-javascript";
import { sql, StandardSQL } from "@codemirror/lang-sql";
import { syntaxTree } from "@codemirror/language";
import { linter } from "@codemirror/lint";
import type { Extension } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  type EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import { clsx } from "clsx";
import * as eslint from "eslint-linter-browserify";
import { type Dispatch, type FC, type SetStateAction, useCallback, useRef } from "react";

import type { Attribute } from "../../../graphql.js";
import { getAttributeLabel } from "../../../redux/modules/sample-admin.js";

// eslint-disable-next-line unicorn/prefer-global-this
window.process = { cwd: () => "" }; // workaround for letting the linter run through

export enum ScriptingLanguage {
  JAVASCRIPT,
  SQL,
}

export interface IScriptingInputFieldProps {
  scriptingLanguage?: ScriptingLanguage;
  initialValue?: string;
  disabled?: boolean;
  className?: string;
  isLoading?: boolean;
  acceptedMacros?: SCRIPTING_MACRO[];
  height?: string; // fixed height if necessary

  attributes?: Attribute[];
  onChangeScript?: (value: string) => void;
  setSyntaxError: Dispatch<SetStateAction<string>>;
}
export const parentTypeNameMacroRegex = /\$PARENT_TYPE\(\)/g;
export const currentValueMacroRegex = /\$SELF\(\)/g;
export const currentDateMacroRegex = /\$DATE\([^)\t\n\r\f]*?\)/g;
export const nextMacroRegex = /\$(NEXT\([^)\t\n\r\f]*?\))/g;
export const countMacroRegex = /\$(COUNT\([^)\t\n\r\f]*?\))/g;
export const scriptAttributeRegex = /\$([0-9]+)/g;
export const hexaColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/g; // matching the full string because we try to match against string nodes

export enum SCRIPTING_MACRO {
  PARENT_TYPE,
  DATE,
  SELF_VALUE,
}

interface ITemplateMacro {
  macro: SCRIPTING_MACRO;
  label: string;
  regex: RegExp;
  defaultVariable: string;
  description?: string;
}
const templateFunctions: ITemplateMacro[] = [
  {
    macro: SCRIPTING_MACRO.SELF_VALUE,
    label: "My Value",
    regex: currentValueMacroRegex,
    defaultVariable: `$SELF()`,
    description: `(Refer to the current value)`,
  },
  {
    macro: SCRIPTING_MACRO.PARENT_TYPE,
    label: "Parent Type Name",
    regex: parentTypeNameMacroRegex,
    defaultVariable: `$PARENT_TYPE()`,
    description: `(Include the name of the Parent Type)`,
  },
  {
    macro: SCRIPTING_MACRO.DATE,
    label: "Submission Date",
    regex: currentDateMacroRegex,
    defaultVariable: `$DATE(format=yyyy-MMM-dd hh:mm:ss:SSS)`,
    description: `(Include the creation date with the provided format)`,
  },
];

const functionsCompletion = (acceptedMacros: SCRIPTING_MACRO[]) => (context: CompletionContext) => {
  const word = context.matchBefore(/\w*/);
  if (word.from == word.to && !context.explicit) return null;
  return {
    from: word.from,
    options: templateFunctions
      .filter((templateFunction: ITemplateMacro) => acceptedMacros.includes(templateFunction.macro))
      .map((templateFunction: ITemplateMacro) => ({
        label: templateFunction.label,
        type: "function",
        apply: templateFunction.defaultVariable,
        detail: templateFunction.description,
        boost: 99,
      })),
  };
};

const ScriptingInputField: FC<IScriptingInputFieldProps> = (props) => {
  // auto complete with attributes
  const autoCompleteStartPosition = useRef<number>(null);
  const attributesCompletion = useCallback(
    (context: CompletionContext) => {
      // set the initial position when opening the suggestions dropdown
      if (context.explicit) {
        autoCompleteStartPosition.current = context.pos;
      }
      if (autoCompleteStartPosition.current == context.pos && !context.explicit) return null;
      return {
        from: autoCompleteStartPosition.current,
        options: props.attributes.map((attribute: Attribute) => {
          return {
            label: getAttributeLabel(attribute),
            type: "variable",
            apply: `$${attribute.id}`,
            detail: `(${attribute.attributeType.name})`,
          };
        }),
      };
    },
    [props.attributes],
  );

  const extensions: Extension[] = [
    attributeDecorationsPlugin(props.attributes),
    templateMacroDecorationsPlugin(templateFunctions),
    colorDecorationsPlugin,
  ];

  const linterExtension = useCallback((view: EditorView) => {
    const lint = esLint(new eslint.Linter(), { nashorn: true, es6: false });
    const diagnostics = lint(view);

    // There are different kind of severity https://codemirror.net/docs/ref/#lint.Diagnostic.severity
    const error = diagnostics.find((diagnostic) => diagnostic.severity === "error");
    props.setSyntaxError(error?.message);

    return diagnostics;
  }, []);

  if (props.scriptingLanguage === ScriptingLanguage.JAVASCRIPT) {
    extensions.push(javascript({ jsx: false }));
    if (!props.disabled) {
      // The read only mode (when disabled), usually in Cell, is a single line of code and so the linter may complain
      // because we don't care the correctness of the JS in this case. It's just to have a fast overview.
      extensions.push(linter(linterExtension));
    }
  } else if (props.scriptingLanguage === ScriptingLanguage.SQL) {
    extensions.push(sql({ dialect: StandardSQL }));
  }

  if (props.attributes) {
    const completions = [attributesCompletion];
    if (props.acceptedMacros) {
      completions.push(functionsCompletion(props.acceptedMacros));
    }
    extensions.push(
      autocompletion({
        activateOnTyping: false, // only use Ctrl+Space
        // if the field is disabled, we do not want users to open the language-specific/autocompletion options by shift-space
        override: props.disabled ? [] : completions,
      }),
    );
  }

  return (
    <div
      className={clsx(props.className, "cm-script-editor", { "cm-disabled": props.disabled || props.isLoading })}
      data-testid="script-input"
    >
      <CodeMirror
        readOnly={props.disabled || props.isLoading}
        value={props.isLoading ? "Please wait..." : props.initialValue}
        height={props.height}
        // minHeight="50px"
        maxHeight="300px"
        autoFocus={false}
        extensions={extensions}
        onChange={props.onChangeScript}
      />
    </div>
  );
};

const attributeDecorationsPlugin = (attributes: Attribute[]) =>
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = attributeDecorations(view, attributes);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged)
          this.decorations = attributeDecorations(update.view, attributes);
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );

function attributeDecorations(view: EditorView, attributes: Attribute[]) {
  const widgets = [];

  for (const { from, to } of view.visibleRanges) {
    const script = view.state.sliceDoc(from, to);

    // get all attributes by regex
    const attributesIdMatching = [...script.matchAll(scriptAttributeRegex)];
    if (attributesIdMatching.length > 0) {
      for (const attributesIMatching of attributesIdMatching) {
        const attributeId = Number(attributesIMatching[1]);

        const attribute = attributes.find((a) => a.id == attributeId);
        const deco = Decoration.widget({
          widget: new AttributeDecoration(attribute),
          side: 1,
        });
        const widgetPosition = attributesIMatching.index + attributesIMatching[0].length;
        widgets.push(deco.range(widgetPosition));
      }
    }
  }
  return Decoration.set(widgets);
}

class AttributeDecoration extends WidgetType {
  constructor(readonly attribute: Attribute) {
    super();
  }

  eq(other: AttributeDecoration) {
    return other.attribute?.id == this.attribute?.id;
  }

  toDOM() {
    const wrap = document.createElement("span");
    wrap.setAttribute("aria-hidden", "true");
    let textNode = null;
    if (this.attribute) {
      wrap.className = "cm-widget cm-attribute-id";
      textNode = document.createTextNode(getAttributeLabel(this.attribute));
    } else {
      wrap.className = "cm-widget cm-attribute-unknown";
      textNode = document.createTextNode("unknown");
    }
    // eslint-disable-next-line unicorn/prefer-dom-node-append
    wrap.appendChild(textNode);
    return wrap;
  }

  ignoreEvent() {
    return true;
  }
}

const colorDecorationsPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = colorDecorations(view);
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) this.decorations = colorDecorations(update.view);
    }
  },
  {
    decorations: (v) => v.decorations,
  },
);

function colorDecorations(view: EditorView) {
  const widgets = [];
  for (const { from, to } of view.visibleRanges) {
    syntaxTree(view.state).iterate({
      from,
      to,
      enter: (node) => {
        if (node.name == "String") {
          const colorMatching = view.state.doc.sliceString(node.from + 1, node.to - 1).match(hexaColorRegex);
          if (colorMatching) {
            const hexaColor = colorMatching[0];
            const deco = Decoration.widget({
              widget: new ColorDecoration(hexaColor),
              side: 1,
            });
            widgets.push(deco.range(node.to));
          }
        }
      },
    });
  }
  return Decoration.set(widgets);
}

class ColorDecoration extends WidgetType {
  constructor(readonly color: string) {
    super();
  }

  eq(other: ColorDecoration) {
    return other.color == this.color;
  }

  toDOM() {
    const wrap = document.createElement("span");
    wrap.setAttribute("aria-hidden", "true");
    wrap.className = "cm-widget cm-color-widget";
    wrap.setAttribute("style", "background-color:" + this.color);
    return wrap;
  }

  ignoreEvent() {
    return true;
  }
}

const templateMacroDecorationsPlugin = (templateMacros: ITemplateMacro[]) =>
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = macroDecorations(view, templateMacros);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged)
          this.decorations = macroDecorations(update.view, templateMacros);
      }
    },
    {
      decorations: (v) => v.decorations,
    },
  );

function macroDecorations(view: EditorView, templateMacros: ITemplateMacro[]) {
  const widgets = [];

  templateMacros.forEach((templateMacro: ITemplateMacro) => {
    for (const { from, to } of view.visibleRanges) {
      const script = view.state.sliceDoc(from, to);

      // get all attributes by regex
      const macroMatching = [...script.matchAll(templateMacro.regex)];

      if (macroMatching.length > 0) {
        for (const macro of macroMatching) {
          const deco = Decoration.widget({
            widget: new MacroDecoration(templateMacro),
            side: 1,
          });
          const widgetPosition = macro.index + macro[0].length;
          widgets.push(deco.range(widgetPosition));
        }
      }
    }
  });

  return Decoration.set(widgets, true);
}

class MacroDecoration extends WidgetType {
  constructor(readonly templateMacro: ITemplateMacro) {
    super();
  }

  eq(other: MacroDecoration) {
    return other?.templateMacro.label == this.templateMacro?.label;
  }

  toDOM() {
    const wrap = document.createElement("span");
    wrap.setAttribute("aria-hidden", "true");
    let textNode = null;
    wrap.className = "cm-widget cm-template-macro";
    textNode = document.createTextNode(this.templateMacro.label);
    // eslint-disable-next-line unicorn/prefer-dom-node-append
    wrap.appendChild(textNode);
    return wrap;
  }

  ignoreEvent() {
    return true;
  }
}

export default ScriptingInputField;
